/**
 * Player model + pure state transitions — ported from RLPlayer.py.
 *
 * All operations are pure (return a new state) so the Worker can persist
 * snapshots to D1 without aliasing bugs. This also fixes the original
 * `Actions.get_current_points` double-counting bug (we always recompute from
 * scratch) and persists `experiencedEvents` (the original save dropped it).
 */

import type { Action, Item, GameEvent, PlayerState, StatMap } from "./types.js";

/** Luck (气运) a fresh player starts with — funds the first reroll or two. */
export const STARTING_LUCK = 2;
/** Hard cap on banked luck, so it stays scarce and rerolls keep their cost. */
export const MAX_LUCK = 5;

/**
 * Cost of a reroll given its 1-based index within a single check: the 1st reroll
 * costs 1, the 2nd costs 2, etc. Escalating, so spamming rerolls is self-limiting
 * even for a luck-rich player.
 */
export const rerollCost = (rerollIndex: number): number => Math.max(1, rerollIndex);
export const extraDiceCost = (extraDice: number): number => {
  const n = Math.max(0, Math.trunc(extraDice) || 0);
  return (n * (n + 1)) / 2;
};

export function emptyPlayer(): PlayerState {
  return {
    adjustments: {},
    attainedItems: [],
    experiencedEvents: [],
    flags: {},
    luck: STARTING_LUCK,
  };
}

export function clonePlayer(p: PlayerState): PlayerState {
  return {
    adjustments: { ...p.adjustments },
    attainedItems: [...p.attainedItems],
    experiencedEvents: [...p.experiencedEvents],
    flags: { ...p.flags },
    luck: p.luck ?? STARTING_LUCK,
  };
}

/** Spend luck on a reroll, floored at 0. Pure. */
export function spendLuck(p: PlayerState, amount: number): PlayerState {
  const next = clonePlayer(p);
  next.luck = Math.max(0, (next.luck ?? 0) - amount);
  return next;
}

/** Grant luck (crit success, lucky item), capped at MAX_LUCK. Pure. */
export function grantLuck(p: PlayerState, amount: number): PlayerState {
  const next = clonePlayer(p);
  next.luck = Math.min(MAX_LUCK, (next.luck ?? 0) + amount);
  return next;
}

/** Add a stat delta map onto a player's durable attributes. */
export function applyAdjustments(p: PlayerState, map: StatMap): PlayerState {
  const next = clonePlayer(p);
  for (const [stat, delta] of Object.entries(map)) {
    next.adjustments[stat] = (next.adjustments[stat] ?? 0) + delta;
  }
  return next;
}

/** Set / increment story flags (counters or booleans-as-0/1). */
export function applyFlags(p: PlayerState, map: StatMap): PlayerState {
  const next = clonePlayer(p);
  for (const [flag, delta] of Object.entries(map)) {
    next.flags[flag] = (next.flags[flag] ?? 0) + delta;
  }
  return next;
}

export function canTakeItem(p: PlayerState, item: Item): boolean {
  for (const ex of item.exclude) if (p.attainedItems.includes(ex)) return false;
  for (const rq of item.require) if (!p.attainedItems.includes(rq)) return false;
  return true;
}

export function canTakeEvent(p: PlayerState, event: GameEvent): boolean {
  for (const ex of event.exclude)
    if (p.experiencedEvents.includes(ex)) return false;
  for (const rq of event.require)
    if (!p.experiencedEvents.includes(rq)) return false;
  return true;
}

/** Attain an item (if allowed), applying its stat adjustments. */
export function attainItem(
  p: PlayerState,
  item: Item,
): { player: PlayerState; ok: boolean } {
  if (!canTakeItem(p, item)) return { player: p, ok: false };
  let next = applyAdjustments(p, item.adjustments);
  if (item.luck) next = grantLuck(next, item.luck);
  next.attainedItems.push(item.id);
  return { player: next, ok: true };
}

export function markEventExperienced(
  p: PlayerState,
  eventId: string,
): PlayerState {
  if (p.experiencedEvents.includes(eventId)) return p;
  const next = clonePlayer(p);
  next.experiencedEvents.push(eventId);
  return next;
}

/**
 * Points the player brings to an action: Σ adjustments[stat] * coefficient.
 * Always recomputed from scratch (fixes the original accumulation bug).
 */
export function computeCurrentPoints(action: Action, p: PlayerState): number {
  let points = 0;
  for (const [stat, coeff] of Object.entries(action.adj)) {
    if (stat in p.adjustments) points += p.adjustments[stat] * coeff;
  }
  return points;
}
